// react
import type { ReactNode } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui icons
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import LocalFireDepartmentRoundedIcon from '@mui/icons-material/LocalFireDepartmentRounded';
import SolarPowerRoundedIcon from '@mui/icons-material/SolarPowerRounded';
import WaterDropRoundedIcon from '@mui/icons-material/WaterDropRounded';

// material ui
import { Box, Tooltip } from '@mui/material';
import type { GridRenderCellParams } from '@mui/x-data-grid-premium';

type AnalysisType = 'power' | 'water' | 'heat';

interface TypeIconProps {
  params: GridRenderCellParams;
}

const TypeIcon = ({ params }: TypeIconProps) => {
  const { t } = useTranslation('portfolio-analysis');
  if ('children' in params.rowNode && params.rowNode.children.length > 0) {
    const types: Record<string, boolean> = {};
    params.rowNode.children.forEach((childId: string | number) => {
      const child = params.api.getRow(childId);
      types[child.type] = true;
    });

    return <EnergyBadgedIcon params={params} />;
  }
  const energyType: AnalysisType = params.row.type;
  const icon = ((): ReactNode => {
    if (energyType === 'power') {
      if (params.row.type_of_mp == 'PV') {
        return (
          <Tooltip placement="left" title={t('type_descriptions.solar_panel')}>
            <SolarPowerRoundedIcon
              sx={{ color: (theme) => theme.palette[energyType].main }}
            />
          </Tooltip>
        );
      } else {
        return (
          <Tooltip placement="left" title={t('type_descriptions.power')}>
            <BoltRoundedIcon
              sx={{ color: (theme) => theme.palette[energyType].main }}
            />
          </Tooltip>
        );
      }
    }

    if (energyType === 'water') {
      return (
        <Tooltip placement="left" title={t('type_descriptions.water')}>
          <WaterDropRoundedIcon
            sx={{ color: (theme) => theme.palette[energyType].main }}
          />
        </Tooltip>
      );
    }

    if (energyType === 'heat') {
      return (
        <Tooltip placement="left" title={t('type_descriptions.heat')}>
          <LocalFireDepartmentRoundedIcon
            sx={{ color: (theme) => theme.palette[energyType].main }}
          />
        </Tooltip>
      );
    }
  })();
  return (
    <Box display="flex" alignItems="center" width="100%" height="100%">
      {icon}
    </Box>
  );
};
// material ui icons
import ApartmentRoundedIcon from '@mui/icons-material/ApartmentRounded';
import LanRoundedIcon from '@mui/icons-material/LanRounded';
const EnergyBadgedIcon = ({ params }: TypeIconProps) => {
  const { t } = useTranslation('portfolio-analysis');

  let linkedEnergyGroups: { energyGroupId?: unknown }[] = [];
  if ('children' in params.rowNode && params.rowNode.children.length > 0) {
    params.rowNode.children.forEach((childId: string | number) => {
      const child = params.api.getRow(childId);
      if (child?.linked_energy_groups?.length > 0) {
        linkedEnergyGroups = linkedEnergyGroups.concat(
          child.linked_energy_groups,
        );
      }
    });
    // remove duplicates
    const uniqueIds = new Set();
    linkedEnergyGroups = linkedEnergyGroups.filter((group) => {
      if (uniqueIds.has(group?.energyGroupId)) {
        return false;
      }
      uniqueIds.add(group?.energyGroupId);
      return true;
    });
  }
  const hasLinkedEnergyGroups = params.row.linked_energy_groups?.length > 0;
  return (
    <Tooltip
      placement="left"
      title={
        hasLinkedEnergyGroups
          ? t('type_descriptions.matrikel_with_linked_energy_groups')
          : t('type_descriptions.matrikel')
      }
    >
      <Box
        position="relative"
        display="flex"
        height={'100%'}
        alignContent={'center'}
      >
        {hasLinkedEnergyGroups ? (
          <LanRoundedIcon
            sx={{
              color: (theme) =>
                theme.palette.mode === 'dark'
                  ? theme.palette.grey[200]
                  : theme.palette.grey[400],
              my: 'auto',
              mx: 'auto',
            }}
            fontSize="medium"
          />
        ) : (
          <ApartmentRoundedIcon
            sx={{ color: 'divider', my: 'auto', mx: 'auto' }}
            fontSize="medium"
          />
        )}
        {/* {types?.power && (
          <CircleRoundedIcon
            sx={{
              position: 'absolute',
              top: HEIGHT,
              left: 0,
              fontSize: SIZE,
              color: (theme) => theme.palette.power.main,
            }}
          />
        )}
        {types?.water && (
          <CircleRoundedIcon
            sx={{
              position: 'absolute',
              top: HEIGHT,
              left: '62%',
              transform: 'translateX(-50%)',
              fontSize: SIZE,
              color: (theme) => theme.palette.water.main,
            }}
          />
        )}
        {types?.heat && (
          <CircleRoundedIcon
            sx={{
              position: 'absolute',
              top: HEIGHT,
              right: -5,
              fontSize: SIZE,
              color: (theme) => theme.palette.heat.main,
            }}
          />
        )} */}
      </Box>
    </Tooltip>
  );
};

export default TypeIcon;
